/**
 * Reading the protocol for what a CRF field must carry (split out of crfAuthority.ts so
 * the CRF Builder approval gate can import it: it is pure, and crfAuthority reaches the
 * CDISC catalog, which the browser build cannot).
 * Moved verbatim; crfAuthority.ts re-exports every name, so no caller changes.
 */

import { namedSeverityScale, printedSeverityInStatement } from "./standardScales";

export type AuthorityConcept =
  | "sex" | "race" | "ethnicity" | "severity" | "causality" | "outcome" | "action"
  | "disposition" | "yesno" | "pregnancyResult" | "dipstickResult";

export type CodeLayer = "sponsor dictionary" | "protocol" | "CDASH" | "platform default";

export interface AuthOption { code: number | string; label: string }
export interface ResolvedList { options: AuthOption[]; layer: CodeLayer; source: string }


/** The shape this module reads and writes. Anything else on the variable rides along untouched. */
export interface AuthVariable {
  varName: string;
  questionText: string;
  responseType: string;
  options?: Array<{ code?: number | string; label: string }>;
  codesFrom?: string;
  [k: string]: unknown;
}

// ── Reading the protocol ───────────────────────────────────────────────────

export interface Sourced { text: string; section?: string; field?: string; codes?: AuthOption[] }

/**
 * An option label that repeats its own code ("0 = Normal", "97 - Not done" on the option coded 0 / 97) shows the
 * code twice on the form ("0=0 = Normal"). Returns the label without that leading "<code> =" or "<code> -".
 */
export function stripOwnCode(code: number | string | undefined, label: string): string {
  const c = String(code ?? "").trim();
  if (!c) return label;
  const esc = c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // "<code> =" always repeats the code. A dash repeats it only as a separator (a space, then a non-digit): "1-2 times", "0-3 months" and "1 - 2 times" are ranges.
  const m = new RegExp(`^\\s*${esc}\\s*=\\s*(?=\\S)|^\\s*${esc}\\s+[-–—]\\s+(?=\\D)`).exec(label);
  if (!m) return label;
  const rest = label.slice(m[0].length);
  return rest.charAt(0).toLowerCase() === rest.charAt(0) ? cap(rest) : rest;
}

export function factsOf(analysis: Record<string, unknown> | null | undefined, key: string): Sourced[] {
  const raw = (analysis ?? {})[key];
  if (!Array.isArray(raw)) return [];
  const out: Sourced[] = [];
  for (const f of raw) {
    if (typeof f === "string" && f.trim()) out.push({ text: f.trim() });
    else if (f && typeof f === "object") {
      const t = (f as { text?: unknown }).text;
      const s = (f as { section?: unknown }).section;
      const field = (f as { field?: unknown }).field;
      const rawCodes = (f as { codes?: unknown }).codes;
      const codes: AuthOption[] = Array.isArray(rawCodes)
        ? rawCodes.flatMap((c) => {
            const code = (c as { code?: unknown })?.code;
            const label = (c as { label?: unknown })?.label;
            return (typeof code === "string" || typeof code === "number") && String(code).trim() !== "" && typeof label === "string" && label.trim()
              ? [{ code, label: stripOwnCode(code, label.trim()) }] : [];
          })
        : [];
      if (typeof t === "string" && t.trim()) {
        out.push({
          text: t.trim(),
          ...(typeof s === "string" && s.trim() ? { section: s.trim() } : {}),
          ...(typeof field === "string" && field.trim() ? { field: field.trim() } : {}),
          ...(codes.length >= 2 ? { codes } : {}),
        });
      }
    }
  }
  return out;
}

export const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const OPEN = "([";
const CLOSE = ")]";

/** Split on separators that sit OUTSIDE brackets and quotes: "Mild (itch; rash); Marked" has two pieces, not three. */
export function splitOutsideBrackets(text: string, isSep: (ch: string, rest: string) => boolean): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote = "";
  let cur = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quote) { if (ch === quote) quote = ""; cur += ch; continue; }
    // A quote opens only at the start of a word, so an apostrophe inside "sponsor's" is not a quote.
    if ((ch === '"' || ch === "“") && (i === 0 || /[\s(\[,;:]/.test(text[i - 1]))) { quote = ch === "“" ? "”" : ch; cur += ch; continue; }
    if (OPEN.includes(ch)) depth++;
    else if (CLOSE.includes(ch) && depth > 0) depth--;
    if (depth === 0 && isSep(ch, text.slice(i + 1))) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

/** Remove balanced (...) and [...] groups, innermost first. */
const stripBrackets = (s: string): string => {
  let prev = "";
  let cur = s;
  while (cur !== prev) { prev = cur; cur = cur.replace(/\([^()[\]]*\)|\[[^()[\]]*\]/g, " "); }
  return cur;
};

/** Trim punctuation around a label, but keep a bracket that closes one the label opened. */
export const clean = (s: string): string => {
  let t = s.replace(/\s+/g, " ").trim();
  for (let guard = 0; guard < 20; guard++) {
    const before = t;
    t = t.replace(/^[\s"'\-–—]+/, "").replace(/[\s"'.,:;]+$/, "");
    const opens = (t.match(/[([]/g) ?? []).length;
    const closes = (t.match(/[)\]]/g) ?? []).length;
    if (closes > opens && /[)\]]$/.test(t)) t = t.slice(0, -1);
    else if (opens > closes && /^[([]/.test(t)) t = t.slice(1);
    if (t === before) break;
  }
  return t.trim();
};

/** "1=Yes, 0=No" or "Grade 1 = mild; Grade 2 = moderate": every piece must be a code=label pair. */
export function parseCodePairs(text: string): AuthOption[] | null {
  // The whole list wrapped in brackets: "a 3-point scale (1 = mild, 2 = moderate, 3 = severe)".
  const wrapped = /[([]([^()[\]]*=[^()[\]]*=[^()[\]]*)[)\]]/.exec(text);
  if (wrapped && !text.replace(wrapped[0], " ").includes("=")) return parseCodePairs(wrapped[1]);
  const pieces = splitOutsideBrackets(text, (ch) => ch === ";" || ch === "\n")
    .flatMap((p) => splitOutsideBrackets(p, (ch, rest) => ch === "," && /^\s*\S{1,12}\s*=/.test(rest)));
  const out: AuthOption[] = [];
  for (const piece of pieces) {
    const m = /(?:^|\s)(\S{1,8})\s*=\s*(.+)$/.exec(piece.trim());
    if (!m) { if (out.length > 0 || pieces.length === 1) return null; continue; }
    // Wrapping punctuation is not part of a code: "(1 = mild" has the code 1.
    let code: number | string = m[1].replace(/^[^A-Za-z0-9+]+|[^A-Za-z0-9+]+$/g, "");
    if (!code) return null;
    let label = clean(m[2]);
    if (!label || label.length > 120) return null;
    const gradeBefore = /grade\s*$/i.test(piece.slice(0, piece.indexOf(m[1])).trim());
    if (/^\d+$/.test(String(code))) {
      code = Number(code);
      if (gradeBefore) label = `Grade ${code} - ${cap(label)}`;
    }
    label = stripOwnCode(code, label);
    out.push({ code, label: gradeBefore ? label : cap(label) });
  }
  const codes = new Set(out.map((o) => String(o.code)));
  return out.length >= 2 && codes.size === out.length ? out : null;
}

/** "mild, moderate or severe" -> Mild, Moderate, Severe. A bracketed explanation never contributes items. */
export function parseLabelList(text: string): string[] | null {
  let body = stripBrackets(text);
  const colon = body.lastIndexOf(":");
  if (colon > 0 && /[,;/]|\bor\b/i.test(body.slice(colon + 1))) body = body.slice(colon + 1);
  else {
    const m = /\b(?:graded as|rated as|classified as|as|into|using|categories|scale of|graded)\s+(.+)$/i.exec(body);
    if (m && /[,;/]|\bor\b/i.test(m[1])) body = m[1];
  }
  const items = body
    .split(/[,;/]|\s+\bor\b\s+|\s+\band\b\s+/i)
    .map((s) => clean(s.replace(/^(?:either|or|and)\s+/i, "")))
    .filter(Boolean);
  if (items.length < 2 || items.length > 10) return null;
  if (items.some((i) => i.split(/\s+/).length > 6 || i.length > 60)) return null;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const i of items) {
    const k = i.toLowerCase();
    if (seen.has(k)) return null;
    seen.add(k);
    out.push(cap(i));
  }
  return out;
}

export const sectionOf = (facts: Sourced[]): string => {
  const s = facts.find((f) => f.section)?.section;
  return s ? `protocol section ${s.replace(/^(section|§)\s*/i, "")}` : "protocol";
};

/** Codes 1..n in the order the protocol states them. */
const numbered = (labels: string[]): AuthOption[] => labels.map((label, i) => ({ code: i + 1, label }));

/** The fact's own parsed codes when the extractor gave them, else code=label pairs read from its text. */
function factPairs(f: Sourced): AuthOption[] | null {
  return f.codes && f.codes.length >= 2 ? f.codes : parseCodePairs(f.text);
}

/**
 * A severity or causality list is made of RECOGNISED words, or it is not a
 * scale at all: prose such as "graded 1 to 5 per CTCAE v5.0, where Grade 3 or
 * higher requires reporting" splits into pieces that are not categories.
 */
export const SEVERITY_LABEL = /^(?:mild|moderate|severe|life[- ]threatening|disabling|death|fatal|grade\s*[1-5](?:\s*[-:(]?\s*(?:mild|moderate|severe|life[- ]threatening|disabling|death|fatal)\)?)?)$/i;
export const CAUSALITY_LABEL = /related|unrelated|unlikely|possib|probab|definite|certain|plausible|suspected|doubtful|no relationship|not suspected|conditional/i;

/** The label of a fact that states ONE level of a scale: the text before its bracketed explanation. */
const levelLabel = (text: string): string => clean(text.split(/\s*[:(\[]\s*|\s+-\s+|\.\s/)[0]);

/** One scale: from one fact as pairs, one category per fact, or from one fact as a list. Lists must pass `valid`. */
function scaleFromFacts(facts: Sourced[], valid: (label: string) => boolean): { options: AuthOption[]; facts: Sourced[] } | null {
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs) return { options: pairs, facts: [f] };
  }
  // One level per fact ("Possible (a plausible time relationship, but another cause is plausible)"): an
  // explanation with a comma in it is not a second level, so this reading comes before a list reading.
  if (facts.length >= 2) {
    const labels = facts.map((f) => levelLabel(f.text));
    if (labels.every((l) => l && l.split(/\s+/).length <= 6 && !/[,;/]|\bor\b/i.test(l) && valid(l)) && new Set(labels.map((l) => l.toLowerCase())).size === labels.length) {
      return { options: numbered(labels.map(cap)), facts };
    }
  }
  for (const f of facts) {
    const list = parseLabelList(f.text);
    if (list && list.every(valid)) return { options: numbered(list), facts: [f] };
  }
  return null;
}

const GRADE_WORD = /^(?:mild|moderate|severe|life[- ]threatening|disabling|death|fatal)\b/i;

/**
 * A grade scale held as ONE FACT PER GRADE ("Grade 1 mild", ..., "Grade 5 death related to AE"): the levels
 * become "Grade 1 (mild)" with the grade as the code. Facts that are not a grade level are ignored (a closing
 * note about the scale); fewer than two levels, or a repeated grade, is not a scale.
 */
function gradeLevelsFromFacts(facts: Sourced[]): { options: AuthOption[]; facts: Sourced[] } | null {
  const levels = new Map<number, string>();
  const used: Sourced[] = [];
  for (const f of facts) {
    const m = /^grade\s*([1-5])\b[\s:\-\u2013(]*(.*)$/i.exec(f.text.trim());
    if (!m || !GRADE_WORD.test(m[2])) continue;
    const n = Number(m[1]);
    if (levels.has(n)) return null;
    const word = clean(m[2].split(/\s+(?:related|due|attributed|resulting)\b|[(;,]/i)[0]).toLowerCase();
    levels.set(n, `Grade ${n} (${word})`);
    used.push(f);
  }
  if (levels.size < 2) return null;
  return { options: [...levels.entries()].sort((a, b) => a[0] - b[0]).map(([code, label]) => ({ code, label })), facts: used };
}

const isYes = (l: string) => /^yes\b|^y$/i.test(l.trim());
const isNo = (l: string) => /^no\b|^n$/i.test(l.trim());

/** The protocol's coding conventions, as sourced facts (the fact class may be absent). */
function conventions(analysis: Record<string, unknown> | null | undefined): Sourced[] {
  return factsOf(analysis, "codingConventions");
}

function yesNoFromProtocol(analysis: Record<string, unknown> | null | undefined): ResolvedList | null {
  const facts = conventions(analysis);
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs && pairs.some((o) => isYes(o.label)) && pairs.some((o) => isNo(o.label))) {
      const yes = pairs.find((o) => isYes(o.label))!;
      const no = pairs.find((o) => isNo(o.label))!;
      return { options: [{ code: yes.code, label: "Yes" }, { code: no.code, label: "No" }], layer: "protocol", source: sectionOf([f]) };
    }
    if (/\bY\s*\/\s*N\b/.test(f.text) || /\byes\s*\/\s*no\b.*\bY\b.*\bN\b/i.test(f.text)) {
      return { options: [{ code: "Y", label: "Yes" }, { code: "N", label: "No" }], layer: "protocol", source: sectionOf([f]) };
    }
    if (/\b1\s*\/\s*0\b/.test(f.text) && /yes|no/i.test(f.text)) {
      return { options: [{ code: 1, label: "Yes" }, { code: 0, label: "No" }], layer: "protocol", source: sectionOf([f]) };
    }
  }
  return null;
}

function sexFromProtocol(analysis: Record<string, unknown> | null | undefined): ResolvedList | null {
  for (const f of conventions(analysis)) {
    const pairs = factPairs(f);
    if (pairs && pairs.some((o) => /^male/i.test(o.label)) && pairs.some((o) => /^female/i.test(o.label))) {
      return { options: pairs, layer: "protocol", source: sectionOf([f]) };
    }
    if (/\bsex\b/i.test(f.text) && /\bM\s*\/\s*F\b/.test(f.text)) {
      return { options: [{ code: "M", label: "Male" }, { code: "F", label: "Female" }], layer: "protocol", source: sectionOf([f]) };
    }
  }
  return null;
}

/** A coded list for a topic ("pregnancy test result", "urine dipstick") out of the coding conventions. */
function topicFromProtocol(analysis: Record<string, unknown> | null | undefined, topic: RegExp): ResolvedList | null {
  const facts = conventions(analysis).filter((f) => topic.test(`${f.field ?? ""} ${f.text}`));
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs) return { options: pairs, layer: "protocol", source: sectionOf([f]) };
  }
  for (const f of facts) {
    const list = parseLabelList(f.text);
    if (list) return { options: numbered(list), layer: "protocol", source: sectionOf([f]) };
  }
  return null;
}

/** The field a convention is about: its stored field name, else the words before the first colon or equals sign. */
export const conventionField = (f: Sourced): string => f.field ?? f.text.split(/[:=]/)[0];

/** Where in the conventions each governed concept is named (matched on the field, never on the label text). */
const FIELD_TOPIC: Partial<Record<AuthorityConcept, RegExp>> = {
  race: /\brace\b|racial/i,
  ethnicity: /ethnic|hispanic|latino/i,
  action: /(?:study[- ]?drug|treatment|drug|dose)\s+action|action\s+(?:taken|with)/i,
  sex: /\bsex\b|\bgender\b/i,
  outcome: /\boutcome\b/i,
};

/** What an action field is about: "device", "other", "drug" (drug, IMP, study treatment or the study's own product), or null. */
export function actionKind(text: string, analysis: Record<string, unknown> | null | undefined): "device" | "other" | "drug" | null {
  const t = text.toLowerCase();
  if (/\bdevices?\b/.test(t)) return "device";
  if (/\bother\b/.test(t)) return "other";
  if (/\b(?:study[- ]?drugs?|drugs?|imp|investigational (?:medicinal )?products?|study treatment|treatment|dose)\b/.test(t)) return "drug";
  const names = factsOfNames(analysis);
  return names.some((n) => t.includes(n)) ? "drug" : null;
}

/** The study's own intervention names, lower case (4+ characters, so an initial never matches). */
function factsOfNames(analysis: Record<string, unknown> | null | undefined): string[] {
  const raw = (analysis ?? {}).interventions;
  if (!Array.isArray(raw)) return [];
  return raw.map((i) => String(typeof i === "string" ? i : (i as { name?: unknown })?.name ?? "").toLowerCase().replace(/\s*\(.*$/, "").trim()).filter((n) => n.length >= 4);
}

/**
 * An action convention applies to a variable only when both name the same thing (drug with drug, device with
 * device, other with other). A variable that names something the convention does not, or nothing while the
 * convention names a product, keeps the model's list. With no variable to compare, any action convention applies.
 */
function sameProduct(concept: AuthorityConcept, f: Sourced, analysis: Record<string, unknown> | null | undefined, v?: AuthVariable): boolean {
  if (concept !== "action" || !v) return true;
  return actionKind(conventionField(f), analysis) === actionKind(`${v.questionText ?? ""} ${v.varName ?? ""}`, analysis);
}

/** A coded list for a concept, from the coding conventions whose FIELD names the concept. */
export function fieldTopicFromProtocol(analysis: Record<string, unknown> | null | undefined, concept: AuthorityConcept, v?: AuthVariable): ResolvedList | null {
  const topic = FIELD_TOPIC[concept];
  if (!topic) return null;
  const facts = conventions(analysis).filter((f) => topic.test(conventionField(f)) && sameProduct(concept, f, analysis, v));
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs) return { options: pairs, layer: "protocol", source: sectionOf([f]) };
  }
  for (const f of facts) {
    const list = parseLabelList(f.text);
    if (list) return { options: numbered(list), layer: "protocol", source: sectionOf([f]) };
  }
  return null;
}

/** "Race (collected at some sites only [NAT]: alfa, beta and gama)": the list after the last colon, or null. */
function listAfterColon(text: string): string[] | null {
  const idx = text.lastIndexOf(":");
  // Two lists in one statement (one per country or site group) cannot be told apart here: no list.
  if (idx < 0 || text.indexOf(":") !== idx) return null;
  const tail = text.slice(idx + 1).replace(/[)\]\s.]+$/, "");
  if (!/[,;]|\band\b|\bor\b/i.test(tail)) return null;
  const items = splitOutsideBrackets(tail, (ch) => ch === "," || ch === ";")
    .flatMap((p) => p.split(/\s+(?:and|or)\s+/i))
    .map((x) => clean(x.replace(/^(?:and|or)\s+/i, "")))
    .filter(Boolean);
  if (items.length < 2 || items.length > 12 || items.some((i) => i.split(/\s+/).length > 5)) return null;
  return new Set(items.map((i) => i.toLowerCase())).size === items.length ? items.map(cap) : null;
}

/** A demographic item the protocol collects with its categories listed ("Race (...: a, b and c)"). */
function demographicsList(analysis: Record<string, unknown> | null | undefined, topic: RegExp): ResolvedList | null {
  for (const f of factsOf(analysis, "demographicsFields")) {
    if (!topic.test(f.text.split(/[(:]/)[0])) continue;
    const list = listAfterColon(f.text);
    if (list) return { options: numbered(list), layer: "protocol", source: sectionOf([f]) };
  }
  return null;
}

/**
 * The protocol names a list for this concept but it could not be read with confidence: the text of that
 * statement, so the caller keeps the model's list (and says so) instead of substituting CDASH.
 */
export function unreadableProtocolList(concept: AuthorityConcept, analysis: Record<string, unknown> | null | undefined, v?: AuthVariable): string | null {
  if (protocolList(concept, analysis, v)) return null;
  const topic = FIELD_TOPIC[concept];
  if (topic) {
    const f = conventions(analysis).find((x) => topic.test(conventionField(x)) && sameProduct(concept, x, analysis, v));
    if (f) return sectionOf([f]);
    if (concept === "race" || concept === "ethnicity") {
      const d = factsOf(analysis, "demographicsFields").find((x) => topic.test(x.text.split(/[(:]/)[0]) && /:/.test(x.text));
      if (d) return sectionOf([d]);
    }
    return null;
  }
  const key = concept === "severity" ? "aeSeverityScale" : concept === "causality" ? "aeCausalityScale" : null;
  if (key) {
    const f = factsOf(analysis, key)[0];
    if (f) return sectionOf([f]);
  }
  return null;
}

/** A causality statement worded as one question about a "reasonable possibility" (ICH E2A / FDA IND safety wording). */
const REASONABLE_POSSIBILITY = /\breasonable\s+(?:causal\s+)?possibility\b/i;
/** ...that also gives the second answer: "versus no reasonable possibility", "/ no reasonable possibility", "yes/no", "yes or no". */
const SECOND_ANSWER = /\b(?:no|not\s+a?|without\s+a?)\s+reasonable\s+(?:causal\s+)?possibility\b|\byes\s*(?:\/|or)\s*no\b/i;

const GRADED_LABEL = /\b(?:possibly|probably|definitely|unlikely|certainly|doubtful)\b/i;

/** Two or more distinct causality level words in one statement: a scale, not a bare yes/no question. */
function hasLevelWords(text: string): boolean {
  const words = text.toLowerCase().match(/\b(?:not related|unrelated|possibly related|probably related|definitely related|unlikely|possible|probable|definite|certain|related)\b/g) ?? [];
  return new Set(words).size >= 2;
}

/** The statement without the sentences that only ask the reasonable-possibility yes/no question. */
function withoutPossibilityQuestion(text: string): string {
  return text
    .split(/(?<=[.?!])\s+/)
    .filter((sent) => !(REASONABLE_POSSIBILITY.test(sent) && !hasLevelWords(sent) && !/^[A-Z][A-Za-z -]{1,40}:/.test(sent)) && !/\byes\s*(?:\/|or)\s*no\b/i.test(sent))
    .join(" ");
}

/** "Related: a. Possibly related: b. Not related: c.": each level introduced by its label and a colon. */
function colonLevels(text: string): string[] | null {
  const parts = text.split(/(?<=[.;])\s+(?=[A-Z][A-Za-z-]*(?: [A-Za-z-]+){0,3}:\s)/);
  if (parts.length < 2) return null;
  // Sentences such as "Note: ...", "Definition: ..." or "Reference: ICH E2A." are not levels: only segments whose label
  // is a causality level count, and at least two are needed.
  const labels = parts.map((p) => /^([^:]{2,40}):/.exec(p)?.[1]?.trim() ?? "").filter((l) => l && CAUSALITY_LABEL.test(l));
  if (labels.length < 2) return null;
  return new Set(labels.map((l) => l.toLowerCase())).size === labels.length ? labels.map(cap) : null;
}

/** What the protocol states for a concept, or null. */
export function protocolList(concept: AuthorityConcept, analysis: Record<string, unknown> | null | undefined, v?: AuthVariable): ResolvedList | null {
  switch (concept) {
    case "severity": {
      const facts = factsOf(analysis, "aeSeverityScale");
      const s = scaleFromFacts(facts, (l) => SEVERITY_LABEL.test(l)) ?? gradeLevelsFromFacts(facts);
      if (s) return { options: s.options, layer: "protocol", source: sectionOf(s.facts) };
      // The protocol NAMES a published grading scale instead of listing its levels: the grades are the standard's own.
      // A list the protocol prints in a sentence of the statement ("mild, moderate or severe") wins over a scale
      // that the same statement names for something else (labs, a negation).
      for (const f of facts) {
        const words = printedSeverityInStatement(f.text);
        if (words) return { options: numbered(words), layer: "protocol", source: sectionOf([f]) };
      }
      for (const f of facts) {
        const named = namedSeverityScale(f.text);
        if (named) return { options: named.options, layer: "protocol", source: `${sectionOf([f])}; ${named.label} grades${named.multipleVersions ? " (the protocol names more than one version; none recorded)" : ""}` };
      }
      return null;
    }
    case "causality": {
      const all = factsOf(analysis, "aeCausalityScale");
      // LEVELS FIRST. A scale with named levels stays that scale even when the protocol also says a
      // "reasonable possibility" (the yes/no question it asks for expedited reporting, or the wording of a level).
      // Sentences about the reasonable-possibility question are not part of the level list.
      const levelFacts: Sourced[] = all
        .filter((f) => hasLevelWords(f.text) || !(REASONABLE_POSSIBILITY.test(f.text) && SECOND_ANSWER.test(f.text)))
        .map((f) => ({ ...f, text: withoutPossibilityQuestion(f.text) }))
        .filter((f) => f.text.trim() !== "");
      const colon = all.map((f) => ({ f, levels: colonLevels(f.text) })).find((x) => x.levels);
      if (colon) return { options: numbered(colon.levels!), layer: "protocol", source: sectionOf([colon.f]) };
      // A graded label (possibly / probably / unlikely / definitely) anywhere means this is not a related / not related pair.
      const graded = levelFacts.some((f) => GRADED_LABEL.test(f.text));
      const twoWay = graded ? undefined : levelFacts.find((f) => /\brelated\s*(?:\/|or|,)\s*not related\b/i.test(f.text));
      if (twoWay) return { options: numbered(["Related", "Not related"]), layer: "protocol", source: sectionOf([twoWay]) };
      const s = scaleFromFacts(levelFacts, (l) => CAUSALITY_LABEL.test(l));
      if (s) return { options: s.options, layer: "protocol", source: sectionOf(s.facts) };
      // "related or not related to the study drug": two categories, however the sentence is worded.
      const binary = graded ? undefined : levelFacts.find((f) => /\brelated\b/i.test(f.text) && /\bnot related\b|\bunrelated\b/i.test(f.text));
      if (binary) return { options: numbered(["Related", "Not related"]), layer: "protocol", source: sectionOf([binary]) };
      // No level labels anywhere: "Is there a reasonable possibility that the study drug caused the event? Yes/No"
      // is ONE yes/no question, on the study's own yes/no codes (its convention, else the platform's 1/0).
      const possibility = all.find((f) => REASONABLE_POSSIBILITY.test(f.text) && SECOND_ANSWER.test(f.text));
      if (possibility) {
        const yn = yesNoFromProtocol(analysis);
        const yes = yn?.options.find((o) => isYes(o.label));
        const no = yn?.options.find((o) => isNo(o.label));
        return { options: [{ code: yes?.code ?? 1, label: "Yes" }, { code: no?.code ?? 0, label: "No" }], layer: "protocol", source: sectionOf([possibility]) };
      }
      return null;
    }
    case "outcome": return topicFromProtocol(analysis, /\boutcome\b/i);
    case "sex": return sexFromProtocol(analysis);
    case "yesno": return yesNoFromProtocol(analysis);
    case "pregnancyResult": return topicFromProtocol(analysis, /pregnan/i);
    case "dipstickResult": return topicFromProtocol(analysis, /dipstick|urinalysis/i);
    case "race": return fieldTopicFromProtocol(analysis, "race") ?? demographicsList(analysis, /\brace\b|racial/i);
    case "ethnicity": return fieldTopicFromProtocol(analysis, "ethnicity") ?? demographicsList(analysis, /ethnic|hispanic|latino/i);
    case "action": return fieldTopicFromProtocol(analysis, "action", v);
    default: return null;
  }
}

// ── Demographic items the protocol does not collect, or lists once per country ──

const DEMO_TOPIC = { race: /\brace\b|racial/i, ethnicity: /ethnic|hispanic|latino/i } as const;
const HEDGE = /\bunless\b|\bexcept\b|beyond what is required|\bwhere\s+(?:required|permitted)|\bif\s+required|\bas required\b/i;
const NO_ITEM = /\bno\s+(?:\w+\s+){0,4}(?:question|field|item|data)s?\b/i;
const NOT_COLLECTED = /\bnot\s+(?:be\s+)?(?:collect|record|captur|ask|obtain|report)/i;
const WHOLE_STUDY = /\b(?:either|both|any|all|every)\s+(?:of\s+the\s+)?(?:countr|sites?)|\banywhere\b|\bat all\b|\bin (?:this|the) study\b/i;
// A statement limited to one place ("in Japan", "Japanese forms", "for the UK sites", "in Brazil only").
// A negation limited to a subgroup, a visit, a form or a format, or followed by "but ... collected", is not study-wide either.
const LIMITED = /\bfor\s+(?:the\s+)?(?:[A-Za-z-]+\s+){0,2}(?:participants?|subjects?|patients?|children|adults|adolescents|cohorts?|subgroups?|arms?|groups?|females?|males?|women|men)\b|\bat\s+(?:the\s+)?(?:[A-Za-z0-9-]+\s+){0,3}(?:visits?|screening|baseline|follow-up)\b|\b(?:on|in)\s+the\s+(?:[A-Za-z-]+\s+){0,3}(?:forms?|pages?|CRFs?)\b|\bas\s+free[- ]text\b|\bbut\b[^.]*\bcollected\b/i;
const ONE_PLACE = /\bin\s+[A-Z][A-Za-z]+|\b[A-Z][a-z]+(?:ese|ian|ish)\s+(?:sites?|forms?|participants?)|\bfor\s+(?:the\s+)?[A-Z][A-Za-z]*\s+sites?\b|\bonly\b/;

function sentencesOf(text: string): string[] {
  return text.split(/(?<=[.!?;])\s+/).map((x) => x.trim()).filter(Boolean);
}

/** A numbered heading line ("11. Demographic and Baseline Data", "8.2 Demographics"): short, no sentence punctuation at the end. */
const NUMBERED_HEADING = /^\s*(\d+(?:\.\d+)*)\.?\s+([A-Z][^\n]{0,90})$/;
const headingOf = (line: string): { depth: number; title: string; label: string } | null => {
  const t = line.trim();
  const m = NUMBERED_HEADING.exec(t);
  if (!m || /[.:;,]$/.test(t)) return null;
  return { depth: m[1].split(".").length, title: m[2], label: t };
};

/**
 * The paragraphs of the full protocol text that can speak about demographics, each with the numbered heading
 * above it: the demographics section when the text has one (a numbered heading that names demographics, up
 * to the next heading as shallow or shallower), otherwise every paragraph.
 */
export function demographicParagraphs(protocolText: string | null | undefined): Array<{ text: string; where: string }> {
  const lines = String(protocolText ?? "").split(/\r?\n/);
  if (lines.every((l) => !l.trim())) return [];
  const nearest: string[] = [];
  let current = "";
  for (const l of lines) { const h = headingOf(l); if (h) current = h.label; nearest.push(current); }
  let from = -1;
  let to = lines.length;
  for (let i = 0; i < lines.length; i++) {
    const h = headingOf(lines[i]);
    if (!h || !/demograph/i.test(h.title)) continue;
    from = i;
    for (let j = i + 1; j < lines.length; j++) {
      const n = headingOf(lines[j]);
      if (n && n.depth <= h.depth) { to = j; break; }
    }
    break;
  }
  const out: Array<{ text: string; where: string }> = [];
  for (let i = from < 0 ? 0 : from; i < (from < 0 ? lines.length : to); i++) {
    if (!lines[i].trim() || headingOf(lines[i])) continue;
    out.push({ text: lines[i].trim(), where: nearest[i] ? `protocol section ${nearest[i].replace(/^(\d+(?:\.\d+)*)\.?\s+/, "$1 ")}` : "protocol text" });
  }
  return out;
}

/**
 * Where the protocol says a demographic item (race or ethnicity) is NOT collected in the study, or null.
 * Only a statement about the whole study counts ("in either country", or no place named), and only when no
 * other sentence collects the item. A statement limited to one country or site group does not.
 *
 * The protocol reading is read first. When it says nothing about the item, the FULL protocol text is read
 * (its demographics section, else whole sentences) with the same rules, because the reading can leave out
 * a sentence the protocol states.
 */
export function demographicNotCollected(analysis: Record<string, unknown> | null | undefined, topic: "race" | "ethnicity", protocolText?: string | null): string | null {
  const re = DEMO_TOPIC[topic];
  let negated: Sourced | null = null;
  for (const f of factsOf(analysis, "demographicsFields")) {
    for (const sentence of sentencesOf(f.text)) {
      if (!re.test(sentence)) continue;
      // A conditional statement ("unless required", "where permitted") is neither a study-wide negation nor a collection.
      if (HEDGE.test(sentence)) continue;
      if (!NOT_COLLECTED.test(sentence) && !NO_ITEM.test(sentence)) {
        // A bare item name or a sentence that collects it: the protocol collects it.
        return null;
      }
      if (!LIMITED.test(sentence) && (WHOLE_STUDY.test(sentence) || !ONE_PLACE.test(sentence))) negated = negated ?? f;
    }
  }
  if (negated) return sectionOf([negated]);
  // The reading did not collect the item and did not negate it for the whole study (it said nothing, hedged,
  // or limited the statement to one place): the full text decides.
  if (!protocolText) return null;
  let found: string | null = null;
  for (const para of demographicParagraphs(protocolText)) {
    for (const sentence of sentencesOf(para.text)) {
      if (!re.test(sentence) || HEDGE.test(sentence)) continue;
      if (!NOT_COLLECTED.test(sentence) && !NO_ITEM.test(sentence)) return null;
      if (!LIMITED.test(sentence) && (WHOLE_STUDY.test(sentence) || !ONE_PLACE.test(sentence))) found = found ?? para.where;
    }
  }
  return found;
}

const PLACE_GROUP = /\b(?:for|in)\s+(?:the\s+)?([A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*)*)\s+(?:sites?|centres?|centers?|countr(?:y|ies))\b/g;
/** "Canadian categories:", "UK ONS categories:": a named list per place, introduced by a colon. */
const CATEGORY_LIST_HEAD = /\b((?:[A-Z][A-Za-z]*\s+){0,3}[A-Z][A-Za-z]*)\s+(?:[a-z]+\s+){0,2}(?:categories|classification|list)\s*:/g;
/** Country names and demonyms (a place label must name one), plus UK, US and EU. */
const COUNTRY_WORDS = new Set((
  "uk us usa eu united kingdom britain british english scottish welsh irish america american canada canadian mexico mexican brazil brazilian argentina argentine chile chilean colombia colombian peru peruvian " +
  "france french germany german italy italian spain spanish portugal portuguese netherlands dutch belgium belgian switzerland swiss austria austrian sweden swedish norway norwegian denmark danish finland finnish poland polish " +
  "russia russian ukraine ukrainian turkey turkish greece greek israel israeli egypt egyptian nigeria nigerian kenya kenyan ghana ghanaian ethiopia ethiopian uganda ugandan tanzania tanzanian zambia zambian zimbabwe zimbabwean " +
  "africa african south india indian pakistan pakistani bangladesh bangladeshi china chinese japan japanese korea korean taiwan taiwanese vietnam vietnamese thailand thai philippines filipino indonesia indonesian malaysia malaysian singapore " +
  "australia australian zealand new saudi arabia arabian iran iranian iraq iraqi australasia european"
).split(/\s+/));
const namesCountry = (label: string): boolean => label.toLowerCase().split(/[^a-z]+/).some((w) => COUNTRY_WORDS.has(w));
/** A named classification source: an adjective before "census", or a statistics office or agency. */
const SOURCE_NAME = [
  /\b(?:the\s+)?([A-Za-z]+)\s+census\b/gi,
  /\b(ONS|IBGE|INSEE|ISTAT|DESTATIS|StatCan)\b/g,
  /\b(?:[A-Z][A-Za-z]*\s+)?(?:Office|Institute|Bureau|Agency|Department|Ministry|Council)\s+(?:for|of)\s+(?:National\s+)?(?:[A-Z][A-Za-z]*\s+(?:and\s+)?){0,4}Statistics\b/g,
];
const SOURCE_STOP = /^(?:the|a|an|national|population|housing|each|every|this|that|of|per)$/i;

const COUNTRY_ALIASES: string[][] = [
  ["uk", "britain", "british", "united kingdom", "english", "scottish", "welsh", "ons"],
  ["us", "usa", "america", "american", "omb"],
  ["canada", "canadian", "statcan"], ["brazil", "brazilian", "ibge"], ["france", "french", "insee"],
  ["italy", "italian", "istat"], ["germany", "german", "destatis"],
];
/** The country a place label stands for ("UK ONS" and "British census" are the UK; "US OMB" is the US). */
function countryKey(label: string): string {
  const words = label.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  for (const w of words) {
    const g = COUNTRY_ALIASES.find((a) => a.includes(w));
    if (g) return g[0];
    if (COUNTRY_WORDS.has(w)) return w;
  }
  return label.toLowerCase();
}
/** One name per country, in order: two lists from the same country are not two places. */
function distinctCountries(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of names) { const k = countryKey(n); if (!seen.has(k)) { seen.add(k); out.push(n); } }
  return out;
}

/** The distinct places or sources a statement gives separate lists for. */
function placesIn(text: string, includePlaceGroups: boolean): string[] {
  const names = new Map<string, string>();
  const add = (n: string) => { const k = n.trim().toLowerCase(); if (k && !names.has(k)) names.set(k, n.trim()); };
  if (includePlaceGroups) for (const m of text.matchAll(PLACE_GROUP)) add(m[1]);
  for (const m of text.matchAll(CATEGORY_LIST_HEAD)) {
    const name = m[1].trim();
    if (namesCountry(name)) add(name);
  }
  // Two named classification sources in one statement (one census, one statistics office).
  const sources = new Map<string, string>();
  for (const re of SOURCE_NAME) {
    for (const m of text.matchAll(re)) {
      const name = (m[1] ?? m[0]).replace(/^the\s+/i, "").trim();
      // A source counts when a country is named with it: a national statistics acronym, or a country adjective before "census" or a statistics office.
      const national = /^(?:ONS|IBGE|INSEE|ISTAT|DESTATIS|StatCan)$/.test(name);
      if (!SOURCE_STOP.test(name) && (national || namesCountry(name))) sources.set(name.toLowerCase(), name);
    }
  }
  if (sources.size >= 2) for (const n of sources.values()) add(n);
  return [...names.values()];
}

/**
 * The protocol gives a separate list of categories per country or site group for this item (two or more
 * places named in the statements about it), or null. Returns a short description for the field note.
 *
 * Detected: "for the X sites ... for the Y sites"; "<Place> categories: ... <Other place> categories: ...";
 * two named classification sources in one statement (census, a statistics office). The stored reading is read
 * first, then the full protocol text (demographics section, else every paragraph that names the item).
 */
export function perCountryDemographicList(analysis: Record<string, unknown> | null | undefined, topic: "race" | "ethnicity", protocolText?: string | null): string | null {
  const re = DEMO_TOPIC[topic];
  // Places named across ALL the statements about the item (one statement per country is common).
  const places = new Map<string, string>();
  let first: Sourced | null = null;
  // Separate facts that each give ONE place's list for the item (one fact per country) count together.
  const oneEach = new Map<string, string>();
  for (const f of factsOf(analysis, "demographicsFields")) {
    if (!re.test(f.text.split(/[(:]/)[0]) || !/:/.test(f.text)) continue;
    if (NOT_COLLECTED.test(f.text)) continue;
    const head = f.text.split(/:/)[0];
    for (const p of [...placesIn(head, true), ...placesIn(f.text, false)]) {
      if (!places.has(p.toLowerCase())) places.set(p.toLowerCase(), p);
      first = first ?? f;
    }
    // A fact that names one classification source of its own ("Canadian census ...: a; b", "UK ONS ...: x; y").
    for (const re2 of SOURCE_NAME) for (const m of head.matchAll(re2)) {
      const name = (m[1] ?? m[0]).replace(/^the\s+/i, "").trim();
      const national = /^(?:ONS|IBGE|INSEE|ISTAT|DESTATIS|StatCan)$/.test(name);
      if (!SOURCE_STOP.test(name) && (national || namesCountry(name))) { oneEach.set(name.toLowerCase(), name); first = first ?? f; }
    }
  }
  // A source already inside a named place ("ONS" inside "UK ONS") is the same place, not a third.
  if (oneEach.size >= 2) for (const [k, n] of oneEach) if (![...places.keys()].some((p) => p.includes(k) || k.includes(p))) places.set(k, n);
  const countries = distinctCountries([...places.values()]);
  if (countries.length >= 2 && first) return `${countries.join(" and ")} lists, ${sectionOf([first])}`;
  if (!protocolText) return null;
  for (const para of demographicParagraphs(protocolText)) {
    if (!re.test(para.text) || NOT_COLLECTED.test(para.text)) continue;
    const named = distinctCountries(placesIn(para.text, true));
    if (named.length >= 2) return `${named.join(" and ")} lists, ${para.where}`;
  }
  return null;
}

/** True when the text names a device (an action field about a device takes no drug list). */
export function namesDevice(text: string): boolean {
  return /\bdevices?\b/i.test(text);
}

/** True when the list carries drug-dose wording ("dose", "drug"). */
export function hasDrugDoseWording(options: Array<{ label: string }> | undefined): boolean {
  return (options ?? []).some((o) => /\bdoses?\b|\bdrugs?\b/i.test(o.label));
}

export const isSelect = (v: AuthVariable) => v.responseType === "single_select" || v.responseType === "multi_select";

/** The concepts whose lists belong to adverse-event-family forms (pregnancy outcome shares only the outcome concept). */
const AE_CONCEPTS: ReadonlySet<AuthorityConcept> = new Set<AuthorityConcept>(["severity", "causality", "outcome", "action"]);
const AE_FAMILY_FORM = /death report|device incident|product complaint|hospitali[sz]ation|special interest|important medical|\bSUSARs?\b|\breactions?\b|overdose|treatment[- ]emergent|adverse|\bSAEs?\b|\bAEs?\b|\bAESIs?\b|\bADEs?\b|\bUADEs?\b|\bSADEs?\b|device deficienc|serious (?:event|incident)|safety event/i;
const PREGNANCY_FORM = /pregnan/i;

/**
 * Whether the AE-family concepts (outcome, severity, relationship or causality, action taken) belong on a form.
 * They describe an EVENT, so they apply only on an adverse event, serious adverse event, AESI, adverse device
 * effect or device deficiency form (pregnancy outcome only for outcome). With no form name the answer is yes
 * (callers that do not know the form keep the old behaviour).
 */
export function aeConceptAppliesOnForm(concept: AuthorityConcept, formName: string | undefined | null): boolean {
  if (!AE_CONCEPTS.has(concept)) return true;
  const f = String(formName ?? "").trim();
  if (!f) return true;
  if (AE_FAMILY_FORM.test(f)) return true;
  return concept === "outcome" && PREGNANCY_FORM.test(f);
}

/** The governed concept a variable carries, or null. Looks at the name and the text, never the options. A form name makes the AE-family concepts form-aware. */
export function classifyVariable(v: AuthVariable, formName?: string | null): AuthorityConcept | null {
  const concept = classifyVariableByWords(v);
  return concept && aeConceptAppliesOnForm(concept, formName) ? concept : null;
}

function classifyVariableByWords(v: AuthVariable): AuthorityConcept | null {
  const name = String(v.varName || "").toUpperCase();
  const text = String(v.questionText || "").toLowerCase();
  if (v.responseType === "yesno") return "yesno";
  if (!isSelect(v)) return null;
  if (/non-study|concomitant/.test(text)) return null;
  if (/^(SEX|GENDER)$/.test(name) || /^(sex|gender)\b/.test(text)) return "sex";
  if (/^RACE/.test(name) || /^race\b/.test(text)) return "race";
  if (/^ETHNIC/.test(name) || /^ethnic/.test(text)) return "ethnicity";
  const all = `${name} ${text}`.toLowerCase();
  if ((/pregnan/.test(all) || /^PREG/.test(name)) && /result|res\b|outcome/.test(all)) return "pregnancyResult";
  if (/dipstick/.test(text) && /result|protein|glucose|blood|leuk|nitrite|ketone|ph\b|bilirubin/.test(text)) return "dipstickResult";
  if (/(^|[^A-Z])(AE|SAE)(SEV|GR|TOXGR)/.test(name) || /\b(severity|intensity)\b/.test(text) || /\b(event|ae|sae)\s+grade\b/.test(text)) return "severity";
  if (/^(AE|SAE)REL/.test(name) || /\b(relationship|causality|relatedness)\b/.test(text)) return "causality";
  if (/^(AEOUT|SAEOUT)/.test(name) || /\boutcome\b/.test(text)) return "outcome";
  if (/action taken|action with/.test(text)) return "action";
  if (/primary reason/.test(text) && /(ended|discontinu|withdr|stopp|exit)/.test(text)) return "disposition";
  return null;
}


// ── The dose rule: a protocol that forbids a dose change states an action list by saying so ──

export const DOSE_NOT_PERMITTED = /\bno dose (?:modification|reduction|adjustment|titration|change)s?\b|dose (?:modification|reduction|adjustment|change)s? (?:is|are|will be) not (?:permitted|allowed)|dose (?:modification|reduction|adjustment|change)s? (?:is|are) prohibited|not (?:permitted|allowed) to (?:reduce|modify|adjust|change) (?:the )?dose|(?:only|limited to) (?:a )?(?:dose |treatment )?(?:interruption|interrupt\w*|temporary (?:stop|interruption|discontinuation)|discontinuation)/i;
/** A sentence that PERMITS a dose reduction, increase, titration or level. A sentence that denies one does not count. */
export const DOSE_PERMIT_WORDS = /reduc|increas|decreas|titrat|escalat|dose level/i;
/** A denial limited to a condition ("no dose change is needed for age", "... in renal impairment") is not study-wide. */
export const DOSE_CONDITION_LIMIT = /\b(?:for|in|with|due to|because of)\s+(?:(?:the|a|an|any|patients?|participants?|subjects?|those)\s+)*(?:age\b|ages\b|aged\b|elderly|older|(?:\w+\s+){0,2}impairment|renal|hepatic|kidney|liver|body weight|weight|sex\b|gender|race\b|ethnicity|food|concomitant)/i;
/** True for a sentence that forbids a dose change for the whole study. */
export const isStudyWideDoseDenial = (sentence: string): boolean => DOSE_NOT_PERMITTED.test(sentence) && !DOSE_CONDITION_LIMIT.test(sentence);
export const DOSE_DENIAL = /\bno\b|\bnot\b|\bnever\b|prohibit|\bonly\b|\bwithout\b/i;

/** Everything the reading says under the named keys, as one lower-case string. */
export function corpusOf(analysis: any, keys: string[]): string {
  const parts: string[] = [];
  const walk = (v: unknown, depth: number) => {
    if (v == null || depth > 6) return;
    if (typeof v === "string") parts.push(v);
    else if (typeof v === "number" || typeof v === "boolean") parts.push(String(v));
    else if (Array.isArray(v)) for (const i of v) walk(i, depth + 1);
    else if (typeof v === "object") for (const i of Object.values(v as object)) walk(i, depth + 1);
  };
  for (const k of keys) walk(analysis?.[k], 0);
  return parts.join(" | ").toLowerCase();
}

/**
 * True when the protocol (reading or full text) says the study drug dose may not be changed ("no dose
 * reduction", "dose reduction is not allowed", "only interruption") and nowhere permits a change. That is a
 * statement about the action-taken list, so CDASH's dose-change options must not be offered.
 */
export function protocolForbidsDoseChange(analysis: Record<string, unknown> | null | undefined, protocolText?: string | null): boolean {
  const corpus = `${corpusOf(analysis, ["interventions", "safety", "treatment", "dosing", "dosageModifications", "doseModification", "doseModifications", "stoppingRules"])} | ${String(protocolText ?? "").toLowerCase()}`;
  const sentences = corpus.split(/[.;|\n]+/).map((x) => x.trim()).filter(Boolean);
  const denies = sentences.some(isStudyWideDoseDenial);
  const permits = sentences.some((x) => /dose/.test(x) && DOSE_PERMIT_WORDS.test(x) && !DOSE_DENIAL.test(x));
  return denies && !permits;
}
